import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Stat, Section, Loading, Pill, useFetch, money } from "./_shared";
import { OpsPulse, TodoBoard } from "./_ops";
import { api } from "@/lib/api";
import {
  Flag, ShieldCheck, LifeBuoy, AlertTriangle, CalendarClock, Share2, UserPlus,
  Sparkles, RefreshCw, ArrowRight,
} from "lucide-react";

const ICON = {
  reports: Flag, verifications: ShieldCheck, support: LifeBuoy, finance: AlertTriangle,
  campaigns: CalendarClock, affiliate: Share2, signups: UserPlus,
};
const BORDER = {
  bad: "border-l-rose-500", warn: "border-l-amber-500",
  info: "border-l-sky-500", good: "border-l-emerald-500",
};
const SEV_LABEL = { bad: "dringend", warn: "offen", info: "info" };

function AttentionTile({ item, onOpen, onSuggest, suggesting }) {
  const Icon = ICON[item.key] || Flag;
  const done = item.severity === "good" || !item.count;
  return (
    <div
      className={`flex flex-col justify-between rounded-2xl border border-l-4 border-border bg-card p-4 ${BORDER[item.severity] || BORDER.good}`}
      data-testid={`admin-cc-tile-${item.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${done ? "text-emerald-600" : "text-foreground"}`} />
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{item.label}</p>
        </div>
        {done ? <Pill tone="good">erledigt</Pill> : <Pill tone={item.severity}>{SEV_LABEL[item.severity] || "info"}</Pill>}
      </div>
      <div className="mt-3 flex items-end justify-between gap-2">
        <div>
          <p
            className={`font-display text-3xl font-semibold ${done ? "text-emerald-600" : "text-foreground"}`}
            data-testid={`admin-cc-count-${item.key}`}
          >
            {item.count}
          </p>
          {item.key === "affiliate" && item.amount_cents > 0 && (
            <p className="text-xs text-muted-foreground">{money(item.amount_cents)} im Hold</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!done && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-full text-primary"
              title="KI-Vorschlag"
              onClick={() => onSuggest(item.key)}
              disabled={suggesting}
              data-testid={`admin-cc-suggest-${item.key}`}
            >
              <Sparkles className="h-4 w-4" />
            </Button>
          )}
          <Button
            size="sm"
            variant={done ? "ghost" : "outline"}
            className="gap-1 rounded-full text-xs"
            onClick={() => onOpen(item.route)}
            data-testid={`admin-cc-open-${item.key}`}
          >
            {item.cta} <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function renderBriefing(text) {
  const lines = (text || "").split("\n");
  const out = [];
  lines.forEach((raw, i) => {
    const ln = raw.trimEnd();
    if (/^\s*-{3,}\s*$/.test(ln)) { out.push(<hr key={i} className="my-2 border-border" />); return; }
    const h = ln.match(/^\s*(#{1,6})\s+(.*)$/);
    if (h) { out.push(<p key={i} className="mb-1 mt-1 text-sm font-semibold text-foreground">{h[2]}</p>); return; }
    if (!ln.trim()) return;
    const parts = ln.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    out.push(
      <p key={i} className="mb-1 text-sm leading-relaxed text-foreground">
        {parts.map((p, j) =>
          p.startsWith("**") && p.endsWith("**")
            ? <strong key={j}>{p.slice(2, -2)}</strong>
            : <span key={j}>{p}</span>
        )}
      </p>
    );
  });
  return out;
}

export default function Overview() {
  const navigate = useNavigate();
  const { data: o, loading } = useFetch("/admin/overview");
  const { data: ai } = useFetch("/admin/ai-metrics");
  const { data: cc, loading: ccLoading, reload: reloadCC } = useFetch("/admin/command-center");
  const { data: supTop } = useFetch("/support/admin/threads?status=escalated&limit=5");
  const { data: subs } = useFetch("/admin/pre-launch/subscription-audit");
  const { data: diag } = useFetch("/admin/user-diagnostics?limit=1");

  const [briefing, setBriefing] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefErr, setBriefErr] = useState(null);

  const [suggestion, setSuggestion] = useState(null);
  const [suggestKey, setSuggestKey] = useState(null);
  const [suggestErr, setSuggestErr] = useState(null);

  const genBriefing = async () => {
    setBriefLoading(true); setBriefErr(null);
    try {
      const { data } = await api.post("/admin/command-center/ai-briefing");
      setBriefing(data);
      setSuggestion(null);
      if (data.error) setBriefErr("KI derzeit nicht verfügbar — Fallback-Plan angezeigt.");
    } catch {
      setBriefErr("KI-Briefing konnte nicht erstellt werden. Bitte später erneut versuchen.");
    } finally {
      setBriefLoading(false);
    }
  };

  const genSuggest = async (key) => {
    setSuggestKey(key); setSuggestErr(null); setSuggestion(null);
    try {
      const { data } = await api.post("/admin/command-center/ai-suggest", { key });
      setSuggestion(data);
      if (data.error) setSuggestErr("KI derzeit nicht verfügbar — Fallback angezeigt.");
    } catch {
      setSuggestErr("KI-Vorschlag konnte nicht erstellt werden.");
    } finally {
      setSuggestKey(null);
    }
  };

  if (loading) return <Loading />;
  const s = o || {};
  const items = cc?.items || [];

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Admin"
        title="Kommandozentrale"
        subtitle="Voller Überblick + KI-gestützte Priorisierung — alles Wichtige auf einen Blick erreichbar und abarbeitbar."
      />

      <Section
        title={`Handlungsbedarf${cc?.attention_total ? ` · ${cc.attention_total} dringend` : " · alles erledigt"}`}
        testId="admin-command-center"
        action={
          <Button size="sm" variant="ghost" className="gap-1 rounded-full text-xs" onClick={reloadCC} data-testid="admin-cc-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Aktualisieren
          </Button>
        }
      >
        {ccLoading && !cc ? (
          <Loading />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((it) => (
              <AttentionTile
                key={it.key}
                item={it}
                onOpen={(r) => navigate(r)}
                onSuggest={genSuggest}
                suggesting={suggestKey === it.key}
              />
            ))}
          </div>
        )}
      </Section>

      <OpsPulse />
      <TodoBoard />

      {(supTop?.threads?.length ?? 0) > 0 && (
        <Section
          title={`Eskalierte Support-Anfragen${supTop.kpi?.by_status?.escalated ? ` · ${supTop.kpi.by_status.escalated}` : ""}`}
          testId="admin-cc-support"
          action={
            <Button size="sm" variant="ghost" className="gap-1 rounded-full text-xs" onClick={() => navigate("support")} data-testid="admin-cc-support-all">
              Alle ansehen <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          }
        >
          <div className="space-y-1.5">
            {supTop.threads.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card p-3" data-testid={`admin-cc-support-${t.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{t.subject || "Anfrage"}</p>
                  <p className="truncate text-xs text-muted-foreground">{t.user_name} · {t.category}</p>
                </div>
                <Button size="sm" variant="outline" className="shrink-0 gap-1 rounded-full text-xs" onClick={() => navigate(`support?thread=${t.id}`)} data-testid={`admin-cc-support-open-${t.id}`}>
                  Bearbeiten <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section
        title="KI-Kommandozentrale"
        testId="admin-cc-ai"
        action={
          <Button size="sm" className="gap-1.5 rounded-full" onClick={genBriefing} disabled={briefLoading} data-testid="admin-cc-briefing-btn">
            <Sparkles className="h-4 w-4" /> {briefLoading ? "Erstellt …" : "KI-Briefing erstellen"}
          </Button>
        }
      >
        {!briefing && !briefLoading && !suggestion && !suggestKey && (
          <p className="text-sm text-muted-foreground">
            Die KI liest die aktuellen Handlungsbedarf-Zahlen und erstellt einen priorisierten Tagesplan:
            was zuerst, warum und der nächste konkrete Schritt. Für einen gezielten Vorschlag das ✨-Symbol auf einer Kachel antippen.
          </p>
        )}
        {suggestKey && <p className="text-sm text-muted-foreground" data-testid="admin-cc-suggest-loading">KI-Vorschlag wird erstellt …</p>}
        {suggestErr && <p className="mb-2 text-xs text-amber-600" data-testid="admin-cc-suggest-error">{suggestErr}</p>}
        {suggestion && (
          <div className="mb-3 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="admin-cc-suggestion">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">KI-Vorschlag · {suggestion.label} ({suggestion.count})</p>
            {renderBriefing(suggestion.suggestion)}
            <p className="mt-2 text-[11px] text-muted-foreground">Erstellt: {new Date(suggestion.generated_at).toLocaleString("de-DE")}</p>
          </div>
        )}
        {briefErr && <p className="mb-2 text-xs text-amber-600" data-testid="admin-cc-briefing-error">{briefErr}</p>}
        {briefing && (
          <div className="rounded-xl border border-border bg-secondary/40 p-4" data-testid="admin-cc-briefing">
            {renderBriefing(briefing.briefing)}
            <p className="mt-2 text-[11px] text-muted-foreground">Erstellt: {new Date(briefing.generated_at).toLocaleString("de-DE")}</p>
          </div>
        )}
      </Section>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <Stat label="Nutzer" value={s.users ?? "—"} testId="admin-stat-users" />
        <Stat label="Klient:innen" value={s.clients ?? "—"} />
        <Stat label="Coaches" value={s.coaches ?? "—"} />
        <Stat label="Gesperrt" value={s.suspended ?? 0} tone={s.suspended ? "warn" : "default"} />
        <Stat label="Offene Meldungen" value={s.pending_reports ?? 0} tone={s.pending_reports ? "bad" : "good"} testId="admin-stat-reports" />
        <Stat label="Breakouts" value={s.breakouts ?? 0} />
        <Stat label="KI-Analysen" value={s.ai_analyses ?? 0} />
        <Stat label="Beiträge" value={s.posts ?? 0} />
        <Stat label="Kreise" value={s.circles ?? 0} />
      </div>

      {diag && (() => {
        const w = diag.windows || {};
        const pay = diag.paying || {};
        const td = diag.test_demo || {};
        const dupEmail = (diag.duplicates?.by_email_exact || []).length;
        const win = (k) => w[k] || { total: 0, real: 0, anon: 0 };
        return (
          <Section title="Nutzer-Analyse" testId="admin-user-analysis">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Stat label="Konten gesamt" value={diag.total} testId="admin-ua-total" />
              <Stat label="Echte (registriert)" value={diag.real_estimate} tone="good" testId="admin-ua-real" />
              <Stat label="Anonyme Gäste" value={diag.anon_guest} tone="info" testId="admin-ua-anon" />
              <Stat label="Test/Demo/Seed" value={td.count ?? 0} tone="warn" testId="admin-ua-test" />
              <Stat label="Zahlende Kunden" value={pay.total ?? 0} tone={pay.total ? "good" : "default"} testId="admin-ua-paying" />
              <Stat label="Doppelte E-Mails" value={dupEmail} tone={dupEmail ? "bad" : "good"} testId="admin-ua-dupes" />
            </div>

            <div className="mt-3 overflow-hidden rounded-xl border border-border" data-testid="admin-ua-windows">
              <div className="grid grid-cols-4 bg-secondary/60 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <div className="p-2">Neue Konten</div>
                <div className="p-2 text-right">gesamt</div>
                <div className="p-2 text-right">echt</div>
                <div className="p-2 text-right">anonym</div>
              </div>
              {[["7", "7 Tage"], ["30", "30 Tage"], ["90", "90 Tage"]].map(([k, label]) => {
                const r = win(k);
                return (
                  <div key={k} className="grid grid-cols-4 border-t border-border text-sm" data-testid={`admin-ua-win-${k}`}>
                    <div className="p-2 text-foreground">{label}</div>
                    <div className="p-2 text-right font-semibold tabular-nums text-foreground">{r.total}</div>
                    <div className="p-2 text-right tabular-nums text-emerald-600">{r.real}</div>
                    <div className="p-2 text-right tabular-nums text-muted-foreground">{r.anon}</div>
                  </div>
                );
              })}
            </div>

            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground" data-testid="admin-ua-note">
              <b className="text-foreground">So liest du die Zahlen:</b> „Konten gesamt“ = ALLE jemals angelegten Konten (kumulativ,
              inkl. {diag.anon_guest} anonymer Gäste aus dem mobilen „Gratis testen“). „Echte“ = registrierte Nutzer ohne Gäste/Test.
              Die Tabelle zeigt NEUE Konten je Zeitfenster — die „Neue Nutzer“-Kachel entspricht der Zeile „7 Tage · gesamt“
              und enthält anonyme Gäste. „Zahlende Kunden“ zählt nur echte Käufe (RevenueCat + Stripe), NICHT geschenkte Grants.
            </p>
          </Section>
        );
      })()}

      {subs?.buckets_by_source && (() => {
        const b = subs.buckets_by_source;
        const rc = b.revenuecat_iap || 0;
        const web = b.stripe_web || 0;
        const credits = b.credits_internal || 0;
        const grants = (b.founder_grant || 0) + (b.reviewer_grant || 0);
        const paying = rc + web + credits;
        return (
          <Section title="Zahlende Kunden nach Kanal" testId="admin-paying-by-channel">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <Stat label="Zahlend gesamt" value={paying} tone={paying ? "good" : "default"} testId="admin-paying-total" />
              <Stat label="Mobil · RevenueCat" value={rc} testId="admin-paying-revenuecat" />
              <Stat label="Web · Stripe" value={web} testId="admin-paying-stripe" />
              <Stat label="Credits (intern)" value={credits} testId="admin-paying-credits" />
              <Stat label="Lebenslang verschenkt" value={grants} tone="info" testId="admin-paying-grants" />
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground" data-testid="admin-paying-note">
              „Zahlend gesamt“ = RevenueCat (mobile Store-Käufe) + Stripe (Web) + mit Credits bezahlte Abos.
              RevenueCat sieht ausschließlich die mobile Spalte — Stripe/Credits/Grants tauchen dort NIE auf.
              „Lebenslang verschenkt“ (Founder-/Reviewer-Grants) sind keine Käufe. Aktive Abos: {subs.active_count ?? 0} ·
              Lebenslang gesamt: {subs.lifetime_count ?? 0} · {subs.total_users ?? 0} Nutzer geprüft.
            </p>
          </Section>
        );
      })()}

      {ai && (
        <Section title="KI-Nutzung" testId="admin-ai-metrics">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Analysen gesamt" value={ai.total_analyses ?? 0} />
            <Stat label={`Letzte ${ai.period_days ?? 30} T`} value={ai.analyses_last_period ?? 0} />
            <Stat label="Credits (geschätzt)" value={ai.estimated_credits_burnt ?? 0} />
            <Stat label="Sprachen" value={(ai.languages || []).length} />
          </div>
        </Section>
      )}
    </div>
  );
}
